import React, { useState, useEffect } from 'react';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { analyticsService } from '@common/analytics';
import { useLocation, useNavigate } from 'react-router';
import classes from './classes.module.scss';
import FilterDropdown from './filter_dropdown';
import FilterSlider from './filter_slider/filter_slider';
import FilterSwitcher from './filter_switcher';

import {
  readingViaFilters, numberOfReviewsFilters
} from '../../../../config/const';
import withPGTranslation from '../../../../config/withPGTranslation';
import UsePreviousValue from '../../../../../common/config/utils';
import {
  getInitialState, buildQueryString, renderViewForNewAffiliateUser,
  renderCancelBtn, renderFiltersMobileView, getFilterSwitcherId
} from './filter_utils';

const defaultLocalFilterPricePerMinute = {};

function FiltersContainer({
  showPopover,
  popoverView = false,
  dissmissPopover,
  newToPG,
  lastPurchaseDate,
  localFilterPricePerMinute = defaultLocalFilterPricePerMinute,
  currency
}) {
  const { t } = useTranslation();
  const history = useNavigate();
  const location = useLocation();
  const [readingVia, setReadingVia] = useState(() => getInitialState('readingVia', localFilterPricePerMinute));
  const [numberOfReviews, setNumberOfReviews] = useState(() => getInitialState('numberOfReviews', localFilterPricePerMinute));
  const [ppmRange, setPPMRange] = useState(() => getInitialState('ppmRange', localFilterPricePerMinute));
  const [includeOffline, setIncludeOffline] = useState(() => getInitialState('includeOffline', localFilterPricePerMinute));
  const [searchQuery, setSearchQuery] = useState(() => getInitialState('searchQuery', localFilterPricePerMinute));
  const [historySearch, setHistorySearch] = useState(location.search);
  const [showFilters, setShowFilters] = useState(!newToPG);
  const eligibleForNewAffiliateUserView = !lastPurchaseDate && newToPG;
  const [shouldCloseAutocomplete, setShouldCloseAutocomplete] = useState(false);

  const closeFilters = () => {
    setShowFilters(false);
  };

  const openFilters = () => {
    setShowFilters(true);
  };

  useEffect(() => {
    if (popoverView) analyticsService.trackPageVisit('search and filter');
  }, []);

  useEffect(() => {
    setReadingVia(getInitialState('readingVia', localFilterPricePerMinute));
    setNumberOfReviews(getInitialState('numberOfReviews', localFilterPricePerMinute));
    setPPMRange(getInitialState('ppmRange', localFilterPricePerMinute));
    setIncludeOffline(getInitialState('includeOffline', localFilterPricePerMinute));
    setSearchQuery(queryString.parse(location.search).query);
    setHistorySearch(location.search.replace('?', ''));
  }, [location.search, localFilterPricePerMinute]);

  useEffect(() => {
    if (window.currentUserId) setShowFilters(true);
    else if (eligibleForNewAffiliateUserView) setShowFilters(false);
  }, [window.currentUserId, newToPG]);

  const onReadingViaChanged = (id) => { setReadingVia(id); };
  const onNumverOfReviewsChanged = (id) => { setNumberOfReviews(id); };
  const onSliderChanged = (values) => {
    setPPMRange(values);
  };
  const onIncludeOfflineChanged = (value) => { setIncludeOffline(value); };

  const showFilterPopover = () => {
    showPopover({ popoverId: 'filterPopover', history, location });
  };
  const onSearchChanged = (e) => {
    setSearchQuery(e.target.value);
    if (shouldCloseAutocomplete) {
      setShouldCloseAutocomplete(false);
    }
  };

  const onFilterApply = () => {
    setShouldCloseAutocomplete(true);
    const prevHistorySearch = <UsePreviousValue value={ historySearch } />;
    const search = buildQueryString({
      readingVia, numberOfReviews, ppmRange, includeOffline, query: (searchQuery || '').trim(), historySearch
    });
    if (search === prevHistorySearch.props.value) return;
    history(`${ location.pathname }?${ search }`, {
      state: { needRefresh: true, params: (location.state || {}).params },
      replace: true
    });
    if (popoverView) dissmissPopover();
  };

  const onFilterApplyKeyDown = (e) => { if (e.key === 'Enter') onFilterApply(); };

  const renderClickHereBtn = () => (
    <div className={ classes.clickHereText }>
      {t('search_results.need_help')}
      &nbsp;
      <button type="button" className={ classes.clickHereBtn } onClick={ openFilters }>{t('search_results.click_here')}</button>
    </div>
  );

  const showNewToPGSection = () => (eligibleForNewAffiliateUserView && !popoverView ? (
    <div data-testid="new-to-pg-section" className={ classes.newToPGSectionContainer } style={ showFilters ? { borderBottom: '1px solid #EADDED' } : { borderBottom: 'none' } }>
      {renderViewForNewAffiliateUser({
        showFilterPopover, title: t('app_search_results.this_is_how_it_works'), t
      })}
      { showFilters ? null : renderClickHereBtn() }
    </div>
  ) : null);

  const renderFiltersSection = () => (
    <div className={ classes.filterFieldsContainer } style={ eligibleForNewAffiliateUserView || popoverView ? { paddingTop: '0px' } : { paddingTop: '30px' } }>
      <FilterDropdown
        title={ t('home_page.show_only') }
        options={ readingViaFilters }
        selectedOptionId={ readingVia }
        onChange={ onReadingViaChanged }
        dropdownId="readingViaDropdown"
      />
      <FilterDropdown
        title={ t('home_page.number_of_reviews') }
        options={ numberOfReviewsFilters }
        selectedOptionId={ numberOfReviews }
        onChange={ onNumverOfReviewsChanged }
        dropdownId="numberOfReviewsDropdown"
      />
      <FilterSlider currentPPMRange={ ppmRange } onRangeChange={ onSliderChanged } currency={ currency } localFilterPricePerMinute={ localFilterPricePerMinute } />
      <FilterSwitcher
        filterSwitcherId={ getFilterSwitcherId({ popoverView }) }
        checked={ includeOffline }
        onChange={ onIncludeOfflineChanged }
      />
      <button type="button" className={ classes.applyButton } onClick={ onFilterApply }>
        { t('home_page.apply') }
      </button>
    </div>
  );

  return [
    <div
      key="expanded_view"
      className={ popoverView ? classes.popoverFilters : classes.filterContainer }
    >
      {showNewToPGSection()}
      {renderCancelBtn(showFilters, eligibleForNewAffiliateUserView, popoverView, closeFilters)}
      {popoverView && <span className={ classes.searchTitle }>{ t('home_page.filters') }</span>}
      { popoverView || showFilters ? renderFiltersSection() : null}
    </div>,
    renderFiltersMobileView({
      popoverView, showFilterPopover, t, eligibleForNewAffiliateUserView, searchQuery, onSearchChanged, onFilterApplyKeyDown, shouldCloseAutocomplete
    })
  ];
}

export default withPGTranslation(FiltersContainer);
